import {
  SourcingPerformanceStatus,
  SourcingStageStat,
} from 'api-clients/monolith';
import { ErrorBoundary } from 'react-error-boundary';
import colors from 'tailwindcss/colors';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryTheme,
} from 'victory';

const getBarColor = (status?: SourcingPerformanceStatus) => {
  switch (status) {
    case 'goal_met':
      return colors.green[600];
    case 'in_progress':
    case 'on_track':
      return colors.blue[600];
    case 'at_risk':
      return colors.rose[600];
    default:
      return 'lightgray';
  }
};

interface BarChartProps {
  funnelData: SourcingStageStat[];
  labelledBy: string;
}

export function BarChart({ funnelData, labelledBy }: BarChartProps) {
  const parsedFunnelData = funnelData.map(item => ({
    ...item,
    applicants_stats: {
      ...item.applicants_stats,
      expected: item.applicants_stats.expected ?? 0,
      actual: item.applicants_stats.actual ?? 0,
    },
  }));

  const maxValue = Math.max(
    ...parsedFunnelData.flatMap(d => [
      d.applicants_stats.expected,
      d.applicants_stats.actual,
    ]),
    1,
  );

  const tickCount = Math.min(maxValue + 1, 6);
  const yTickValues = [
    ...new Set(
      Array.from({ length: tickCount }, (_, i) =>
        Math.round((i * maxValue) / (tickCount - 1)),
      ),
    ),
  ];

  return (
    <ErrorBoundary fallback={null}>
      <VictoryChart
        containerComponent={<VictoryContainer aria-labelledby={labelledBy} />}
        height={500}
        width={700}
        theme={VictoryTheme.clean}
        domainPadding={{ x: 60 }}
        domain={{ y: [0, maxValue] }}
      >
        {/* X-Axis */}
        <VictoryAxis tickValues={funnelData.map(d => d.title)} />

        {/* Y-Axis */}
        <VictoryAxis
          dependentAxis
          tickValues={yTickValues}
          style={{
            grid: { stroke: colors.neutral[100], strokeWidth: 1 },
          }}
        />

        {/* Background Bars (Expected) */}
        <VictoryBar
          data={parsedFunnelData}
          x="title"
          y="applicants_stats.expected"
          barWidth={40}
          style={{ data: { fill: 'lightgray' }, labels: { fontSize: 10 } }}
          labels={({ datum }: { datum?: SourcingStageStat }) =>
            `${datum?.applicants_stats.actual ?? 0} / ${
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              datum?.applicants_stats.expected || '-'
            }`
          }
        />

        {/* Foreground Bars (Actual) */}
        <VictoryBar
          data={parsedFunnelData}
          x="title"
          y="applicants_stats.actual"
          barWidth={40}
          style={{
            data: {
              fill: ({ datum }: { datum?: SourcingStageStat }) =>
                getBarColor(datum?.performance_status),
            },
          }}
        />
      </VictoryChart>
    </ErrorBoundary>
  );
}
